/**
 * Capability 2: Institution Workspace
 * Purpose: Where institutions onboard consumers, manage requests and monitor progress.
 * "What work do I have to do today?"
 */
import { useEffect, useState } from "react";
import CapabilityLandingPage from "@/components/capability/CapabilityLandingPage";
import api from "@/lib/api";
import {
  LayoutDashboard, Building2, Send, Lightbulb, BarChart3, Code2, Settings2,
} from "lucide-react";

export default function InstitutionWorkspaceCapability() {
  const [kpis, setKpis] = useState({ tenants: null, requests: null, recs: null, reports: null });

  useEffect(() => {
    (async () => {
      const next = { tenants: null, requests: null, recs: null, reports: null };
      try { const { data } = await api.get("/admin/tenants"); next.tenants = Array.isArray(data) ? data.filter((t) => t.status === "active").length : null; } catch { /* endpoint optional */ }
      try { const { data } = await api.get("/trust-requests/kpis"); next.requests = data?.open ?? null; } catch { /* endpoint optional */ }
      try { const { data } = await api.get("/recommendations"); next.recs = Array.isArray(data) ? data.filter((r) => r.status === "open").length : null; } catch { /* endpoint optional */ }
      // Reports run counter is derived from usage, not endpoint — leave placeholder
      setKpis(next);
    })();
  }, []);

  return (
    <CapabilityLandingPage
      eyebrow="Capability 2 · Institution Workspace"
      title="Institution Workspace"
      purpose="Where institutions onboard consumers, manage requests and monitor progress. This is the operational home for every institution — the place that answers 'what work do I have to do today?'"
      primaryActions={[
        { label: "Open Requests", to: "/requests", icon: Send },
        { label: "View Recommendations", to: "/recommendations", icon: Lightbulb },
      ]}
      kpis={[
        { label: "Active tenants", value: kpis.tenants, subLabel: "onboarded" },
        { label: "Open requests", value: kpis.requests, subLabel: "awaiting response" },
        { label: "Open recommendations", value: kpis.recs, subLabel: "to action" },
        { label: "Reports run (30d)", value: null, emptyHint: "Metrics wiring pending" },
      ]}
      subCapabilities={[
        { name: "Institution Dashboard", to: "/dashboard", icon: LayoutDashboard, status: "live",
          description: "Tenant-scoped KPIs, trust trend, and category breakdown." },
        { name: "Institutions", to: "/institutions", icon: Building2, status: "live",
          description: "Platform-admin only. Onboard, configure, and manage every tenant." },
        { name: "Requests", to: "/requests", icon: Send, status: "live",
          description: "Everything starts with a Trust Request. Identity, Consent, KYC, Learning, Affordability." },
        { name: "Recommendations", to: "/recommendations", icon: Lightbulb, status: "live",
          description: "Deterministic rule outcomes explained by AI narrative." },
        { name: "Reports", to: "/reports", icon: BarChart3, status: "live",
          description: "Compliance-grade summary reports, exportable per tenant." },
        { name: "Developer Portal", to: "/developer-portal", icon: Code2, status: "future",
          description: "API keys, event webhooks, sandbox, quotas." },
        { name: "Settings", to: "/institution-settings", icon: Settings2, status: "future",
          description: "Institution-level branding, thresholds and vendor selection." },
      ]}
      relatedCapabilities={[
        { name: "Trust Workflows", to: "/capability/trust-workflows" },
        { name: "Consumer Intelligence", to: "/capability/consumer-intelligence" },
        { name: "Platform · Providers", to: "/capability/platform" },
      ]}
    />
  );
}
